import { useState, useMemo, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import { ArrowRight, Download, Search, X } from 'lucide-react';

const fmtDate = (s) => (s || '').slice(0, 10);
const safe = (s) => (s == null ? '' : String(s));
const pairKey = (r) => `${r.source_id}::${r.target_id}`;

// Backend currently returns `reasons` as a dict like:
//   {name_similarity: 0.96, email_similarity: 0.0, same_phone: true,
//    same_email: false, same_email_domain: true}
// Convert to a small list of human-readable chips.
const formatReasons = (reasons) => {
  if (!reasons) return [];
  if (Array.isArray(reasons)) return reasons.map(String);
  if (typeof reasons !== 'object') return [String(reasons)];
  const chips = [];
  if (reasons.same_phone) chips.push('phone match');
  if (reasons.same_email) chips.push('email exact');
  if (reasons.same_email_domain) chips.push('same domain');
  if (typeof reasons.name_similarity === 'number') {
    chips.push(`name ${Math.round(reasons.name_similarity * 100)}%`);
  }
  if (typeof reasons.email_similarity === 'number' && reasons.email_similarity > 0 && !reasons.same_email) {
    chips.push(`email ${Math.round(reasons.email_similarity * 100)}%`);
  }
  return chips;
};

// Heuristics that produce yellow "review carefully" warnings on a pair.
// These are *advisory only* — they don't deselect the row or block apply.
//
// Signals (any one is enough):
//  - emails differ ONLY by digits (e.g. jeevanhm84@ vs jeevanhm85@) → likely
//    a sibling/family pattern where the same parent registered multiple
//    kids on related emails, NOT one duplicate person
//  - one side is a `parent` and the other a `customer` (cross-role merge —
//    fine, but worth confirming since the customer auto-promotes)
//  - phones don't match AND emails don't match — confidence came purely
//    from name similarity, which is fragile
const stripDigits = (s) => (s || '').replace(/\d+/g, '');
const localOf = (email) => {
  if (!email) return '';
  const at = email.indexOf('@');
  return (at < 0 ? email : email.slice(0, at)).toLowerCase();
};

const computePairWarnings = (r) => {
  const warnings = [];
  const sLocal = localOf(r.source_email);
  const tLocal = localOf(r.target_email);
  const sPhone = (r.source_phone || '').replace(/\D/g, '');
  const tPhone = (r.target_phone || '').replace(/\D/g, '');
  const phoneMatch = sPhone && tPhone && sPhone.slice(-10) === tPhone.slice(-10);
  const emailMatch = sLocal && tLocal && sLocal === tLocal;

  // Digits-only difference in email local part.
  if (
    sLocal && tLocal && sLocal !== tLocal
    && stripDigits(sLocal) === stripDigits(tLocal)
    && stripDigits(sLocal).length >= 3
  ) {
    warnings.push({
      kind: 'digit-diff',
      tone: 'amber',
      label: 'emails differ only by digits',
      tooltip:
        'Email local parts are identical except for digit characters. '
        + 'This often means a parent registered multiple kids on related emails — '
        + 'verify these are the same person before merging.',
    });
  }

  // Cross-role merge.
  if (
    r.source_role && r.target_role && r.source_role !== r.target_role
    && (r.source_role === 'customer' || r.target_role === 'customer')
  ) {
    warnings.push({
      kind: 'cross-role',
      tone: 'sky',
      label: 'cross-role',
      tooltip: `Source is ${r.source_role}, target is ${r.target_role}. Merge will combine the records; the surviving role is whichever is on the target.`,
    });
  }

  // Name-only confidence.
  if (!phoneMatch && !emailMatch) {
    warnings.push({
      kind: 'name-only',
      tone: 'rose',
      label: 'name-only match',
      tooltip:
        'No phone or email match — confidence comes purely from the name similarity. '
        + 'Two strangers can share a name; verify before merging.',
    });
  }

  return warnings;
};

const TONE_CLASSES = {
  amber: 'bg-amber-100 text-amber-800 border border-amber-300',
  sky: 'bg-sky-100 text-sky-800 border border-sky-300',
  rose: 'bg-rose-100 text-rose-800 border border-rose-300',
};

const buildCsv = (rows) => {
  const header = [
    'confidence','reasons',
    'source_role','source_name','source_email','source_phone','source_id','source_created_at',
    'target_role','target_name','target_email','target_phone','target_id','target_created_at',
  ];
  const escape = (v) => {
    const s = safe(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = [header.join(',')];
  rows.forEach((r) => {
    const reasonText = formatReasons(r.reasons).join(' | ');
    lines.push([
      r.confidence,
      reasonText,
      r.source_role, r.source_name, r.source_email, r.source_phone, r.source_id, r.source_created_at,
      r.target_role, r.target_name, r.target_email, r.target_phone, r.target_id, r.target_created_at,
    ].map(escape).join(','));
  });
  return lines.join('\n');
};

const downloadCsv = (rows, filename) => {
  const blob = new Blob([buildCsv(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Full-list preview dialog for the auto-merge plan with row-level
 * checkboxes for selective apply. SOURCE (red) is deleted; TARGET (green)
 * is kept. Default: all pairs at confidence ≥100 are pre-selected; lower
 * confidence rows start unchecked so the admin must opt-in.
 */
export const AutoMergePreviewDialog = ({ open, onOpenChange, preview, onConfirmApply, applying }) => {
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(() => new Set());

  const samples = preview?.samples || [];
  const totalQualifying = preview?.total_qualifying ?? samples.length;
  const shownAll = samples.length >= totalQualifying;

  // Pre-select all confidence-100 pairs whenever a fresh preview comes in.
  // Skip self-pairs (source_id === target_id) — those are invalid and would
  // be rejected by the backend anyway; pre-selecting them is misleading.
  useEffect(() => {
    if (!preview) return;
    const initial = new Set(
      samples
        .filter((r) => (r.confidence ?? 0) >= 100 && r.source_id !== r.target_id)
        .map(pairKey),
    );
    setSelected(initial);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  const filtered = useMemo(() => {
    if (!q.trim()) return samples;
    const needle = q.trim().toLowerCase();
    return samples.filter((r) =>
      [r.source_name, r.source_email, r.target_name, r.target_email]
        .some((v) => safe(v).toLowerCase().includes(needle))
    );
  }, [q, samples]);

  const toggleOne = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const filteredKeys = useMemo(() => filtered.map(pairKey), [filtered]);
  const allFilteredChecked = filteredKeys.length > 0
    && filteredKeys.every((k) => selected.has(k));
  const someFilteredChecked = filteredKeys.some((k) => selected.has(k));

  const toggleAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredChecked) {
        filteredKeys.forEach((k) => next.delete(k));
      } else {
        filteredKeys.forEach((k) => next.add(k));
      }
      return next;
    });
  };

  const checkByConfidence = (minConf) => {
    setSelected(new Set(
      samples
        .filter((r) => (r.confidence ?? 0) >= minConf && r.source_id !== r.target_id)
        .map(pairKey),
    ));
  };

  // Count rows with warnings — surface the total in the "Uncheck flagged"
  // button so admins know how many they're about to drop.
  const flaggedCount = useMemo(
    () => samples.filter((r) => computePairWarnings(r).length > 0).length,
    [samples],
  );

  const uncheckAllFlagged = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      samples.forEach((r) => {
        if (computePairWarnings(r).length > 0) {
          next.delete(pairKey(r));
        }
      });
      return next;
    });
  };

  const selectedRows = useMemo(
    () => samples.filter((r) => selected.has(pairKey(r))),
    [samples, selected],
  );

  const handleApply = () => {
    onConfirmApply(selectedRows);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col" data-testid="automerge-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Auto-Merge Preview
            <Badge variant="destructive">{totalQualifying} pair(s)</Badge>
            <Badge className="bg-emerald-600">{selected.size} selected</Badge>
          </DialogTitle>
          <DialogDescription>
            Tick the pairs you want to merge. <b>SOURCE will be deleted</b>; its children,
            enrollments and bookings transfer to <b>TARGET</b>. Pairs at <b>100% confidence</b>
            are pre-selected for you. Lower-confidence pairs start unchecked.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 px-1">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or email…"
              className="pl-8"
              data-testid="automerge-preview-search"
            />
          </div>
          <div className="flex items-center gap-1 text-xs">
            <span className="text-slate-500 mr-1">Quick select:</span>
            <Button
              variant="outline" size="sm" className="h-7 text-xs"
              onClick={() => checkByConfidence(100)}
              data-testid="automerge-quickselect-100"
            >100%</Button>
            <Button
              variant="outline" size="sm" className="h-7 text-xs"
              onClick={() => checkByConfidence(95)}
              data-testid="automerge-quickselect-95"
            >≥95%</Button>
            <Button
              variant="outline" size="sm" className="h-7 text-xs"
              onClick={() => setSelected(new Set())}
              data-testid="automerge-quickselect-none"
            >None</Button>
            {flaggedCount > 0 && (
              <Button
                variant="outline" size="sm"
                className="h-7 text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
                onClick={uncheckAllFlagged}
                title="Deselect every row that has a warning chip"
                data-testid="automerge-uncheck-flagged"
              >
                ⚠ Uncheck flagged ({flaggedCount})
              </Button>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadCsv(samples, `auto-merge-plan-${Date.now()}.csv`)}
            data-testid="automerge-preview-export-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        <div className="overflow-auto border rounded-md flex-1" data-testid="automerge-preview-table-wrap">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 sticky top-0 z-10">
              <tr className="text-left">
                <th className="px-2 py-2 w-8">
                  <Checkbox
                    checked={allFilteredChecked}
                    onCheckedChange={toggleAllFiltered}
                    aria-label="Select all visible"
                    data-testid="automerge-toggle-all"
                    // Indeterminate state isn't part of shadcn's Checkbox API but
                    // we visually hint at it via opacity.
                    style={{ opacity: !allFilteredChecked && someFilteredChecked ? 0.5 : 1 }}
                  />
                </th>
                <th className="px-2 py-2">Conf.</th>
                <th className="px-2 py-2">Source (will be deleted)</th>
                <th className="px-2 py-2 w-6"></th>
                <th className="px-2 py-2">Target (will be kept)</th>
                <th className="px-2 py-2">Why</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, idx) => {
                const k = pairKey(r);
                const isChecked = selected.has(k);
                const isSelfPair = r.source_id === r.target_id;
                return (
                  <tr
                    key={`${k}-${idx}`}
                    className={`border-t align-top ${isSelfPair ? 'bg-rose-50 opacity-60' : (isChecked ? 'bg-emerald-50/40' : 'hover:bg-slate-50')} ${isSelfPair ? '' : 'cursor-pointer'}`}
                    onClick={() => { if (!isSelfPair) toggleOne(k); }}
                  >
                    <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isChecked}
                        disabled={isSelfPair}
                        onCheckedChange={() => { if (!isSelfPair) toggleOne(k); }}
                        data-testid={`automerge-row-${idx}-cb`}
                      />
                    </td>
                    <td className="px-2 py-2 font-mono">
                      {isSelfPair ? (
                        <span className="text-rose-600 font-bold" title="Same record on both sides — invalid pair">
                          self
                        </span>
                      ) : (
                        <span className={
                          r.confidence >= 100 ? 'text-emerald-700 font-bold'
                          : r.confidence >= 95 ? 'text-emerald-600'
                          : r.confidence >= 85 ? 'text-amber-600'
                          : 'text-slate-600'
                        }>
                          {r.confidence}%
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <div className="font-medium text-rose-700">
                        <a
                          href={r.source_role === 'customer'
                            ? `/admin/customers/${r.source_id}`
                            : `/admin/parents/${r.source_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="hover:underline"
                          data-testid={`automerge-source-link-${idx}`}
                        >
                          {r.source_name || '—'}
                        </a>
                      </div>
                      <div className="text-slate-500">{r.source_email || '—'}</div>
                      <div className="text-slate-400 text-[10px] flex gap-2 flex-wrap">
                        <span>{r.source_role}</span>
                        {r.source_phone && <span>· {r.source_phone}</span>}
                        {r.source_numeric_id && <span>· {r.source_numeric_id}</span>}
                        <span title={r.source_id}>· {String(r.source_id || '').slice(0, 8)}…</span>
                        <span>· created {fmtDate(r.source_created_at)}</span>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-slate-400">
                      <ArrowRight className="h-4 w-4" />
                    </td>
                    <td className="px-2 py-2">
                      <div className="font-medium text-emerald-800">
                        <a
                          href={r.target_role === 'customer'
                            ? `/admin/customers/${r.target_id}`
                            : `/admin/parents/${r.target_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="hover:underline"
                          data-testid={`automerge-target-link-${idx}`}
                        >
                          {r.target_name || '—'}
                        </a>
                      </div>
                      <div className="text-slate-500">{r.target_email || '—'}</div>
                      <div className="text-slate-400 text-[10px] flex gap-2 flex-wrap">
                        <span>{r.target_role}</span>
                        {r.target_phone && <span>· {r.target_phone}</span>}
                        {r.target_numeric_id && <span>· {r.target_numeric_id}</span>}
                        <span title={r.target_id}>· {String(r.target_id || '').slice(0, 8)}…</span>
                        <span>· created {fmtDate(r.target_created_at)}</span>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-slate-600">
                      <div className="flex flex-wrap gap-1">
                        {formatReasons(r.reasons).map((rs, i) => (
                          <span key={`reason-${i}`} className="inline-block px-1.5 py-0 rounded bg-slate-100 text-[10px]">
                            {rs}
                          </span>
                        ))}
                        {computePairWarnings(r).map((w, i) => (
                          <span
                            key={`warn-${i}`}
                            className={`inline-flex items-center gap-1 px-1.5 py-0 rounded text-[10px] font-medium ${TONE_CLASSES[w.tone] || ''}`}
                            title={w.tooltip}
                          >
                            ⚠ {w.label}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-slate-500">
                    {q ? 'No matches for your search.' : 'No qualifying pairs.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="text-xs text-slate-500 px-1">
          Showing {filtered.length}{q ? ` filtered from ${samples.length}` : ''}
          {!shownAll && samples.length < totalQualifying && (
            <span className="text-amber-700 ml-2">
              (only first {samples.length} of {totalQualifying} loaded)
            </span>
          )}
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={applying}>
            <X className="h-4 w-4 mr-1" />
            Close
          </Button>
          <Button
            className="bg-rose-600 hover:bg-rose-700 disabled:opacity-50"
            onClick={handleApply}
            disabled={applying || selected.size === 0}
            data-testid="automerge-preview-confirm-apply-btn"
          >
            {applying
              ? `Merging…`
              : `Merge ${selected.size} Selected Pair${selected.size === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
